import {
  takeEvery,
  put,
  select,
  takeLatest,
  take,
  call,
} from 'redux-saga/effects'
import { Map } from 'immutable'
import * as actions from 'actions/feature/inputs'
import {
  setConditionField,
  SET_CONDITION_FIELD,
} from 'actions/feature/conditions'
import { SET_CAN_ID } from 'actions/feature/outputs'
import {
  makeConditionSelector,
  conditionsSelector,
} from 'selectors/feature/conditions'
import {
  getDisabledConfigFields,
  signalsSelector,
} from 'selectors/feature/inputs'
import { getQueryType } from 'selectors/feature/queries'
import { apiAction } from 'actions/application'
import { updateSelectedInput, switchInputType } from 'actions/feature/inputs'
import { ADD_LOGIC_NODE } from 'actions/feature/logicNodes'
import { makeLogicNode } from 'utils/feature'

import { OPERATORS } from 'components/utils'
import {
  BOOLEAN,
  CARRY,
  SIMPLE,
  DIGITAL_INPUT,
  ANALOG_INPUT,
  ACTIVE_HIGH,
  ACTIVE_LOW,
  CAN_INPUT,
} from 'utils/constants'

import * as subGroups from 'services/subgroups'

export function* watchUpdateSelectedInput() {
  yield takeEvery(actions.UPDATE_SELECTED_INPUT, setDefaultValues)
}

export function* watchGetSubGroups() {
  yield takeEvery(actions.REQUESTING_SUBGROUPS, getSubGroups)
}

export function* watchSwitchInputType() {
  yield takeLatest(actions.SWITCH_INPUT_TYPE, resetCarry)
}

function* setDefaultValues(action) {
  const { uri: conditionUri } = action

  const {
    translationType,
    defaultValue,
    inputConfig,
    subType: conditionSubType,
    translations,
  } = yield select(makeConditionSelector(), {
    queryUri: conditionUri,
  })

  if (translationType === BOOLEAN) {
    yield put(setConditionField(conditionUri, 'operator', OPERATORS.EQUAL))
  }

  const { subType, inputUriOrFloatingCircuit } = action

  const referenceQueryType = yield select(getQueryType, {
    queryUri: inputUriOrFloatingCircuit,
  })

  const isCarryFromLogicSet =
    referenceQueryType === 'complex' && subType === 'carry'

  if (isCarryFromLogicSet) {
    // sets the default evaluation to true if carry is from complex logic set
    yield put(setConditionField(conditionUri, 'result', defaultValue || 1))
  }

  // Get all conditions to check for existing values and custom descriptions
  const allConditions = yield select(conditionsSelector)
  const conditionsMap =
    allConditions instanceof Map ? allConditions.toJS() : allConditions

  // Get the signal URI from the current condition
  const currentCondition = conditionsMap[conditionUri]
  const signalUri = currentCondition?.signal

  const effectiveSubType = conditionSubType || subType

  if (effectiveSubType === DIGITAL_INPUT && signalUri) {
    // Set default value based on polarity
    // Active High: default to 1 (+12V), Active Low: default to 0 (Grounded)
    const isDigitalInputWithActiveHigh = inputConfig === ACTIVE_HIGH
    const valueToSet = isDigitalInputWithActiveHigh ? 1 : defaultValue || 0
    yield put(setConditionField(conditionUri, 'value', valueToSet))

    // Safety net: if this pin has no translations for the current inputConfig
    // (e.g., SSAM_160 with ActiveLow when it only supports ActiveHigh),
    // auto-correct to the valid non-disabled config option.
    if (!translations || translations.length === 0) {
      const disabledConfigs = yield select(getDisabledConfigFields, {
        uri: conditionUri,
      })
      const validConfig = [ACTIVE_HIGH, ACTIVE_LOW].find(
        (cfg) => cfg !== inputConfig && !disabledConfigs.includes(cfg)
      )
      if (validConfig) {
        yield put(setConditionField(conditionUri, 'inputConfig', validConfig))
        const correctedIsActiveHigh = validConfig === ACTIVE_HIGH
        yield put(
          setConditionField(
            conditionUri,
            'value',
            correctedIsActiveHigh ? 1 : defaultValue || 0
          )
        )
      }
    }
  } else if (effectiveSubType === ANALOG_INPUT) {
    // For analog inputs, preserve current value when switching pins
    // Only set default value if there's no current value
    const currentValue = currentCondition?.value
    if (currentValue === undefined || currentValue === null) {
      const valueToSet = defaultValue || 0
      yield put(setConditionField(conditionUri, 'value', valueToSet))
    }
    // If currentValue exists, keep it - don't reset
  } else {
    // Other non-digital inputs: use default value
    const valueToSet = defaultValue || 0
    yield put(setConditionField(conditionUri, 'value', valueToSet))
  }

  if (signalUri) {
    // For 3-position switches the new condition always carries the Up URI, but an
    // existing sibling may be in Down position (different URI).  Resolve the partner
    // URI from relatedInputs so both positions are treated as the same switch.
    const allSignals = yield select(signalsSelector)
    const signalData = allSignals && allSignals.get(signalUri)
    const relatedInputs = signalData && signalData.get('relatedInputs')
    const partnerUri = relatedInputs
      ? Object.keys(relatedInputs.toJS())[0]
      : null

    const existingConditionWithCustomDesc = Object.values(conditionsMap).find(
      (cond) =>
        cond &&
        (cond.signal === signalUri ||
          (partnerUri && cond.signal === partnerUri)) &&
        cond.uri !== conditionUri &&
        cond.customDescription
    )

    if (existingConditionWithCustomDesc?.customDescription) {
      yield put(
        setConditionField(
          conditionUri,
          'customDescription',
          existingConditionWithCustomDesc.customDescription
        )
      )
    }
  }
}

function* getSubGroups() {
  yield put(
    apiAction({
      asyncFn: subGroups.get,
      args: {},
      successActionType: actions.SUBGROUPS_RECEIVED,
      failureActionType: actions.SUBGROUPS_NOT_RECEIVED,
    })
  )
}

function* resetCarry(action) {
  const { queryUri, subType } = action
  if (subType !== CARRY) {
    yield put(setConditionField(queryUri, 'refQueryUri', null))
  }
}

export function* watchAddLogicNodeToInput() {
  yield takeEvery(actions.ADD_LOGIC_NODE_TO_INPUT, handleAddLogicNodeToInput)
}

// Create Logic Node and connect it to a given condition uri
function* handleAddLogicNodeToInput({ nodeType, nextIndex, conditionUri }) {
  const { logicNode, conditions } = makeLogicNode(nodeType, nextIndex)
  yield put({
    type: ADD_LOGIC_NODE,
    nodeType,
    nextIndex,
    logicDetails: {
      logicNode,
      conditions,
    },
  })
  yield put(switchInputType(conditionUri, CARRY, {}, SIMPLE))
  yield put(updateSelectedInput(conditionUri, nodeType, {}, {}, logicNode.uri))
}

export function* watchAddExistingLogicNodeToQuery() {
  yield takeEvery(
    actions.ADD_EXISTING_LOGIC_NODE_TO_QUERY,
    handleAddExistingLogicNodeToQuery
  )
}

function* handleAddExistingLogicNodeToQuery({ node, condition }) {
  yield put(switchInputType(condition.uri, CARRY, {}, node.queryType))
  yield put(updateSelectedInput(condition.uri, node.type, {}, {}, node.uri))
}

export function* watchUpdateTemSwitchCustomDescription() {
  yield takeEvery(
    actions.UPDATE_TEM_SWITCH_CUSTOM_DESCRIPTION,
    synchronizeCustomDescription
  )
}

// Synchronize custom description across all instances of the same signal/CAN input
function* synchronizeCustomDescription({
  uri,
  customDescription,
  conditionUris,
}) {
  // If conditionUris is already provided, the saga has already processed this
  // or it's coming directly from reducer, so skip saga processing
  if (conditionUris) {
    return
  }

  // Get all conditions to access the raw condition
  const allConditions = yield select(conditionsSelector)
  const conditionsMap =
    allConditions instanceof Map ? allConditions.toJS() : allConditions

  // Get the raw condition to extract the signal URI or CAN ID
  const rawCondition = conditionsMap[uri]
  const signalUri = rawCondition?.signal
  const canIdValue = rawCondition?.canId

  if (!signalUri && !canIdValue) {
    // If no signal URI or canId, just update the single condition
    yield put({
      type: actions.UPDATE_TEM_SWITCH_CUSTOM_DESCRIPTION,
      uri,
      customDescription,
      conditionUris: [uri],
    })
    return
  }

  // For 3-position switches, resolve the partner URI so a condition in Down position
  // also syncs to sibling conditions that are in Up position (and vice-versa).
  let partnerUri = null
  if (signalUri) {
    const allSignals = yield select(signalsSelector)
    const signalData = allSignals && allSignals.get(signalUri)
    const relatedInputs = signalData && signalData.get('relatedInputs')
    partnerUri = relatedInputs ? Object.keys(relatedInputs.toJS())[0] : null
  }

  // Find all conditions that use the same signal URI or the same unique CAN input
  // (CAN inputs are unique by the combination of canId + numberOfBits + startBit)
  const numberOfBits = rawCondition?.numberOfBits
  const startBit = rawCondition?.startBit
  const conditionsWithSameInput = Object.values(conditionsMap)
    .filter((cond) => {
      if (!cond) return false
      if (signalUri)
        return (
          cond.signal === signalUri ||
          (partnerUri && cond.signal === partnerUri)
        )
      return (
        cond.canId === canIdValue &&
        // eslint-disable-next-line eqeqeq
        cond.numberOfBits == numberOfBits &&
        cond.startBit === startBit
      )
    })
    .map((cond) => cond.uri)

  // Update all matching conditions with the same custom description
  yield put({
    type: actions.UPDATE_TEM_SWITCH_CUSTOM_DESCRIPTION,
    uri,
    customDescription,
    conditionUris: conditionsWithSameInput,
  })
}

export function* watchSetCanId() {
  yield takeEvery(SET_CAN_ID, inheritCustomDescriptionOnCanIdChange)
}

export function* watchCanInputBitChange() {
  while (true) {
    // Read state BEFORE the next action is processed by the reducer
    // so we can compare the previous value against the incoming value
    const prevConditions = yield select(conditionsSelector)
    const action = yield take(SET_CONDITION_FIELD)
    if (action.paramName !== 'startBit' && action.paramName !== 'numberOfBits')
      continue
    yield call(handleCanInputBitChange, action, prevConditions)
  }
}

// When a CAN ID is set on a condition, inherit any existing custom description
// from another condition that already uses the same CAN ID
function* inheritCustomDescriptionOnCanIdChange({ joinQueryUri, canId }) {
  if (!canId) return

  const allConditions = yield select(conditionsSelector)
  const conditionsMap =
    allConditions instanceof Map ? allConditions.toJS() : allConditions

  // Match on the full unique CAN input identity: canId + numberOfBits + startBit
  const currentCondition = conditionsMap[joinQueryUri]
  const numberOfBits = currentCondition?.numberOfBits
  const startBit = currentCondition?.startBit

  const existingConditionWithCustomDesc = Object.values(conditionsMap).find(
    (cond) =>
      cond &&
      cond.canId === canId &&
      // eslint-disable-next-line eqeqeq
      cond.numberOfBits == numberOfBits &&
      cond.startBit === startBit &&
      cond.uri !== joinQueryUri &&
      cond.customDescription
  )

  if (existingConditionWithCustomDesc?.customDescription) {
    yield put(
      setConditionField(
        joinQueryUri,
        'customDescription',
        existingConditionWithCustomDesc.customDescription
      )
    )
  }
}

// When startBit or numberOfBits changes on a CAN input, the old custom description
// no longer applies (the combination changed). Clear it and try to inherit from
// any existing condition that already uses the new combination.
// prevConditions is the state BEFORE the action was processed by the reducer.
function* handleCanInputBitChange({ uri, paramName, value }, prevConditions) {
  // Always read post-action state for condition type / existence checks.
  // prevCondition may be undefined if the condition was created after the last
  // snapshot (i.e. the while/take loop read prevConditions before this condition existed).
  const allConditions = yield select(conditionsSelector)
  const conditionsMap =
    allConditions instanceof Map ? allConditions.toJS() : allConditions

  const currentCondition = conditionsMap[uri]

  // Only handle CAN input conditions that have a canId set
  if (
    !currentCondition ||
    currentCondition.queryType !== CAN_INPUT ||
    !currentCondition.canId
  )
    return

  // Only skip if we can confirm the value genuinely didn't change.
  // If prevCondition is undefined (newly created condition), always proceed.
  const prevConditionsMap =
    prevConditions instanceof Map ? prevConditions.toJS() : prevConditions
  const prevCondition = prevConditionsMap[uri]
  if (prevCondition) {
    // Use loose equality to handle number/string type mismatch from event.target.value
    // eslint-disable-next-line eqeqeq
    if (prevCondition[paramName] == value) return
  }

  const { canId } = currentCondition
  const numberOfBits = currentCondition.numberOfBits
  const startBit = currentCondition.startBit

  // Clear the description — it belonged to the previous combination
  yield put(setConditionField(uri, 'customDescription', null))

  // Only inherit if the bit position is fully configured (startBit is not null)
  if (startBit === null || startBit === undefined) return

  // Try to inherit a name from another condition with the exact same new combination
  const existingConditionWithCustomDesc = Object.values(conditionsMap).find(
    (cond) =>
      cond &&
      cond.canId === canId &&
      // eslint-disable-next-line eqeqeq
      cond.numberOfBits == numberOfBits &&
      cond.startBit === startBit &&
      cond.uri !== uri &&
      cond.customDescription
  )

  if (existingConditionWithCustomDesc?.customDescription) {
    yield put(
      setConditionField(
        uri,
        'customDescription',
        existingConditionWithCustomDesc.customDescription
      )
    )
  }
}
